import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Breadcrumb, Loader, Nav, Panel } from 'rsuite';
import { AxiosError } from 'axios';
import PageHeader from '@/components/PageHeader';
import { BaseErrorModel, PageState } from '@/models/core.model';
import { PointType } from '@/models/point-type.model';
import { getPointType } from '@/services/point-type.service';
import { toastErrorMessage } from '@/utils/toasterMessage';
import PointTypeForm from './point-type-form';
import PointTypeAiStyleList from './point-type-ai-style-list';

const Page = () => {
  const [activeTab, setActiveTab] = useState('point-type-form');
  const [formObject, setFormObject] = useState<PointType | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const { id: idParam } = useParams();
  const id = idParam ? parseInt(idParam, 10) : undefined;
  const pageState = id ? PageState.Edit : PageState.New;

  useEffect(() => {
    if (id) {
      fetchData(id);
    } else {
      setFormObject(undefined);
      setActiveTab('point-type-form');
    }
  }, [id]);

  const fetchData = async (pointTypeId: number) => {
    setLoading(true);
    try {
      const result = await getPointType(pointTypeId);
      if (result.success) setFormObject(result.result);
    } catch (err) {
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching point type: ${errorModel?.errorMessage}`);
      nav('/point-types');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Panel
      header={
        <PageHeader title={pageState == PageState.Edit && formObject ? `Edit Point Type: ${formObject.name}` : 'New Point Type'}>
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item href="/point-types">Point Types</Breadcrumb.Item>
            <Breadcrumb.Item active>{pageState == PageState.Edit ? 'Edit' : 'New'}</Breadcrumb.Item>
          </Breadcrumb>
        </PageHeader>
      }
    >
      <Nav appearance="subtle" activeKey={activeTab} onSelect={key => setActiveTab(key)}>
        <Nav.Item eventKey="point-type-form">Detail</Nav.Item>
        <Nav.Item disabled={pageState == PageState.New} eventKey="point-type-ai-styles">AI Style Restrictions</Nav.Item>
      </Nav>
      {loading ? <Loader center content="loading ..." /> :
        <>
          {activeTab == 'point-type-form' && <PointTypeForm formObject={formObject} pageState={pageState} />}
          {activeTab == 'point-type-ai-styles' && id && <PointTypeAiStyleList pointTypeId={id} />}
        </>
      }
    </Panel>
  );
};

export default Page;
